import { useEffect, useState } from "react";
import { NavLink, Link } from "react-router-dom";
import { BookOpen, CalendarDays, CalendarRange, Crown, GraduationCap, LayoutDashboard, Library, Megaphone, MessageSquare, Settings } from "lucide-react";
import { useMe } from "@/context/MeContext";

const TABS = [
  { to: "/", label: "Oggi", icon: LayoutDashboard, id: "home" },
  { to: "/diario", label: "Diario", icon: BookOpen, id: "diario" },
  { to: "/orario", label: "Orario", icon: CalendarDays, id: "orario" },
  { to: "/calendario", label: "Calendario", icon: CalendarRange, id: "calendario" },
  { to: "/verifiche", label: "Verifiche", icon: GraduationCap, id: "verifiche" },
  { to: "/bacheca", label: "Bacheca", icon: Megaphone, id: "bacheca" },
  { to: "/portali", label: "Libri digitali", short: "Libri", icon: Library, id: "libri" },
];

function OfflineBanner() {
  const [online, setOnline] = useState(navigator.onLine);
  useEffect(() => {
    const on = () => setOnline(true);
    const off = () => setOnline(false);
    window.addEventListener("online", on);
    window.addEventListener("offline", off);
    return () => {
      window.removeEventListener("online", on);
      window.removeEventListener("offline", off);
    };
  }, []);
  if (online) return null;
  return (
    <div className="fixed inset-x-0 top-0 z-[60] bg-amber-400 pt-[env(safe-area-inset-top)] text-center text-sm font-medium text-black" data-testid="offline-banner">
      Sei offline. Per salvare e aggiornare serve la connessione.
    </div>
  );
}

function TopBar() {
  const { me, isAdmin } = useMe();
  return (
    <header className="sticky top-0 z-40 border-b border-border/60 bg-background/80 pt-[env(safe-area-inset-top)] backdrop-blur-xl">
      <div className="mx-auto flex max-w-6xl items-center justify-between gap-3 px-4 py-3">
        <Link to="/" className="flex min-w-0 items-center gap-2.5" data-testid="topbar-brand">
          <img src="/logo-classe.png" alt="Logo Liceo Scientifico Filolao" className="h-10 w-10 shrink-0 rounded-full bg-white object-contain" data-testid="topbar-logo" />
          <span className="min-w-0">
            <span className="block truncate font-heading text-base font-bold leading-tight" data-testid="topbar-class-name">{me?.class_info?.name}</span>
            {me?.class_info?.school && <span className="block truncate text-xs text-muted-foreground" data-testid="topbar-school-name">{me.class_info.school}</span>}
          </span>
        </Link>
        <div className="flex items-center gap-1.5">
          <span data-testid="topbar-role-pill" className={`inline-flex items-center gap-1 rounded-full px-2 py-1 text-[10px] font-bold uppercase tracking-wide sm:px-2.5 sm:text-[11px] ${isAdmin ? "bg-amber-400/15 text-amber-700 dark:text-amber-300" : "bg-secondary text-secondary-foreground"}`}>
            {isAdmin && <Crown className="h-3 w-3" />}{isAdmin ? <><span className="sm:hidden">Rapp.</span><span className="hidden sm:inline">Rappresentante</span></> : "Alunno"}
          </span>
          <Link to="/messaggi" className="grid h-11 w-11 place-items-center rounded-xl text-muted-foreground transition-colors hover:bg-secondary active:scale-95" data-testid="topbar-messages-btn" aria-label="Messaggi">
            <MessageSquare className="h-5 w-5" />
          </Link>
          <Link to="/altro" className="grid h-11 w-11 place-items-center rounded-xl text-muted-foreground transition-colors hover:bg-secondary active:scale-95" data-testid="nav-tab-impostazioni" aria-label="Impostazioni">
            <Settings className="h-5 w-5" />
          </Link>
        </div>
      </div>
    </header>
  );
}

const TabItem = ({ to, label, short, icon: Icon, id }) => (
  <NavLink
    to={to}
    end={to === "/"}
    data-testid={`nav-tab-${id}`}
    className={({ isActive }) => `flex min-h-14 min-w-0 flex-1 flex-col items-center justify-center gap-1 rounded-xl text-[10px] font-semibold sm:text-[11px] transition-colors active:scale-95 ${isActive ? "text-primary" : "text-muted-foreground"}`}
  >
    {({ isActive }) => (
      <>
        <span className={`grid h-7 w-11 place-items-center rounded-full transition-colors ${isActive ? "bg-primary/10" : ""}`}><Icon className="h-5 w-5" /></span>
        <span className="max-w-full truncate">{short ? <><span className="sm:hidden">{short}</span><span className="hidden sm:inline">{label}</span></> : label}</span>
      </>
    )}
  </NavLink>
);

export default function AppShell({ children }) {
  return (
    <div className="min-h-[100dvh] bg-background">
      <OfflineBanner />
      <TopBar />
      <main className="mx-auto max-w-6xl px-4 pb-[calc(6rem+env(safe-area-inset-bottom))] pt-5">{children}</main>
      <nav className="fixed inset-x-0 bottom-0 z-40 border-t border-border bg-background/95 pb-[env(safe-area-inset-bottom)] backdrop-blur-md" data-testid="bottom-nav">
        <div className="mx-auto flex max-w-3xl px-0.5 py-1">
          {TABS.map((t) => <TabItem key={t.to} {...t} />)}
        </div>
      </nav>
    </div>
  );
}
